import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from 'api/apiClient';
import { SortOrder } from 'hooks/useTableControls';
import { PaginationQueryParams } from 'types/queryTypes';

import {
  DataCaptureListView,
  GetDataCaptureFormByIdResponse,
  ProcessFormNewFormPayload,
  ProcessFormNewFormResponse,
  ProcessFormTemplate,
  ProcessFormTemplateFieldsData,
  SaveDataCaptureFormPayload,
} from 'types/dataCaptureTypes';
import { useUI } from 'contexts/UiContext';
import { MassiveTransactionErrorResponse } from 'types/massiveTransactionTypes';

const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;
const REACT_APP_DARWIN_MASSIVE_LOAD_URL =
  import.meta.env.VITE_DARWIN_MASSIVE_LOAD_URL;
const DATA_CAPTURE_FORMS_QUERY_KEY = 'data-capture-forms';
const DATA_CAPTURE_FORM_BY_ID_QUERY = 'process-map-by-id';
const ERROR_DETAILS_QUERY_KEY = 'error-details';

/**
 * Get all data capture forms
 */
export const useGetAllDataCaptureForms = ({
  companyId,
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'form_name', order: 'ASC' },
}: PaginationQueryParams & { companyId: string } & {
  sortOrder: SortOrder;
}) =>
  useQuery({
    queryKey: [DATA_CAPTURE_FORMS_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<DataCaptureListView[]>(
        `${REACT_APP_DARWIN_URL}/process-forms/${companyId}/form?limit=${limit}&page=${page}&search=${query}&order=${sortOrder.order}&sortBy=${sortOrder.sortBy}`
      ),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

/**
 * Get data capture form by id
 */
export const useGetDataCaptureFormById = (
  companyId: string,
  processFormId: string
) => {
  return useQuery({
    queryKey: [DATA_CAPTURE_FORM_BY_ID_QUERY, companyId, processFormId],
    queryFn: () =>
      fetchFromApi<GetDataCaptureFormByIdResponse>(
        `${REACT_APP_DARWIN_URL}/process-forms/${companyId}/form/${processFormId}`
      ),
    enabled: !!companyId && !!processFormId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Update a data capture form by id
 */
export const useUpdateDataCaptureFormById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      processFormId,
    }: {
      payload: SaveDataCaptureFormPayload;
      companyId: string | number;
      processFormId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-forms/${companyId}/form/${processFormId}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_CAPTURE_FORMS_QUERY_KEY],
      });
    },
  });
};

/**
 * 1. Get form templates
 */
export const useGetFormTemplates = (id?: string) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: ['form-templates', selectedCompany, id],
    queryFn: () =>
      fetchFromApi<ProcessFormTemplate[]>(
        `${REACT_APP_DARWIN_URL}/process-forms/${selectedCompany}/templates/${id}`
      ),
    enabled: !!selectedCompany && !!id,
    refetchOnWindowFocus: false,
  });
};

/**
 * 2. Get form fields
 */
export const useGetTemplateFields = (
  id?: string,
  planId?: string,
  processFlowId?: string
) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: ['template-fields', selectedCompany, id, planId, processFlowId],
    queryFn: () =>
      fetchFromApi<ProcessFormTemplateFieldsData>(
        `${REACT_APP_DARWIN_URL}/process-forms/${selectedCompany}/form/${id}/${planId}/${processFlowId}`
      ),
    enabled: !!selectedCompany && !!id && !!planId && !!processFlowId,
    refetchOnWindowFocus: false,
  });
};

/**
 * 3. Post a new form
 */
export const useCreateNewForm = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: ({ payload }: { payload: ProcessFormNewFormPayload }) =>
      fetchFromApi<ProcessFormNewFormResponse>(
        `${REACT_APP_DARWIN_URL}/process-forms/${selectedCompany}/form`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      // Invalidate the forms list to trigger a refetch if needed
      queryClient.invalidateQueries({
        queryKey: [DATA_CAPTURE_FORMS_QUERY_KEY],
      });
    },
  });
};

// Upload massive load file
export const useUploadMassiveLoadFile = () => {
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: async ({
      file,
      traceabilityPlanId,
      formId,
      processMapName,
      formName,
      processName,
      environment,
      dataSourceId,
    }: {
      file: File;
      traceabilityPlanId: string;
      formId: string;
      processMapName: string;
      formName: string;
      processName: string;
      environment: string;
      dataSourceId: string;
    }) => {
      const params = new URLSearchParams({
        processName,
        formName,
        processMapName,
        dataSourceId,
      });
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetchFromApi(
        `${REACT_APP_DARWIN_MASSIVE_LOAD_URL}/api/massive-loads/companies/${selectedCompany}/plans/${traceabilityPlanId}/forms/${formId}/environment/${environment}/uploads?${params.toString()}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      return response;
    },
  });
};

/** Get data about error details in massive load
 */
export const useGetMassiveLoadErrorDetails = ({
  massiveLoadId,
}: {
  massiveLoadId: string | undefined;
}) => {
  return useQuery({
    queryKey: [ERROR_DETAILS_QUERY_KEY, massiveLoadId],
    queryFn: () =>
      fetchFromApi<MassiveTransactionErrorResponse[]>(
        `${REACT_APP_DARWIN_MASSIVE_LOAD_URL}/api/massive-loads/${massiveLoadId}/details`
      ),
    enabled: !!massiveLoadId,
    refetchOnWindowFocus: false,
  });
};

/** Delete data capture form by id
 */
export const useDeleteDataCaptureFormById = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: (processFormId: string | number) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/process-forms/${selectedCompany}/form/${processFormId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DATA_CAPTURE_FORMS_QUERY_KEY],
      });
    },
  });
};
